// Photos are shrunk on the phone before they're uploaded. A 12 MP phone photo
// (3–4 MB) goes as a JPEG about 2560 px on its long side (about 0.8 MB): the
// upload is about 4× smaller, and tracking numbers and SKUs on boxes stay
// readable even in a wide shot of several boxes. The photo comes out the right
// way up and keeps the time it was taken and the phone's make and model (see
// photoDetails.js), not its GPS location.
//
// Files that aren't photos (PDFs, sheets…), photos that are already small
// enough and photos this browser can't open (HEIC on Android, for one) go as
// they are, so a file is never lost: at worst it uploads as it does today.
// So do all photos on browsers older than 2020, which would save a shrunk
// photo on its side (see drawsPhotosUpright).

import {
  PHOTO_DETAILS_BYTES,
  readPhotoDetails,
  withPhotoDetails,
} from "./photoDetails";

export const PHOTO_OPTIONS = {
  // The longest side of a shrunk photo, in pixels.
  maxSide: 2560,
  // JPEG quality, 0–1.
  quality: 0.85,
  // A photo no bigger than maxSide and no heavier than this goes as it is.
  keepBytes: 1.5 * 1024 * 1024,
  // How long opening or shrinking one photo may take before it goes as it is.
  timeoutMs: 30000,
};

const PHOTO_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/pjpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
];
const PHOTO_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".jpe",
  ".png",
  ".webp",
  ".heic",
  ".heif",
];
const JPEG_TYPES = ["image/jpeg", "image/jpg", "image/pjpeg"];
const JPEG_EXTENSIONS = [".jpg", ".jpeg", ".jpe"];

// ".jpg" for "IMG 1.JPG"; "" when the name has no extension.
export const extensionOf = (name) => {
  const text = String(name ?? "");
  const dot = text.lastIndexOf(".");
  return dot === -1 ? "" : text.slice(dot).toLowerCase();
};

const typeOf = (file) => String(file?.type ?? "").toLowerCase();

// A photo this module may shrink: by its type, or by its name when the
// browser gave no type.
export const isPhoto = (file) =>
  typeOf(file)
    ? PHOTO_TYPES.includes(typeOf(file))
    : PHOTO_EXTENSIONS.includes(extensionOf(file?.name));

const isJpeg = (file) =>
  typeOf(file)
    ? JPEG_TYPES.includes(typeOf(file))
    : JPEG_EXTENSIONS.includes(extensionOf(file?.name));

// The size that fits `width` × `height` within `maxSide`, never larger.
export const fitWithin = (width, height, maxSide) => {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

// "IMG_1234.HEIC" → "IMG_1234.jpg".
export const jpegName = (name) => {
  const text = String(name ?? "photo");
  const dot = text.lastIndexOf(".");
  return `${dot > 0 ? text.slice(0, dot) : text}.jpg`;
};

export const readBytes = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });

const withTimeout = (promise, ms) => {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error("Took too long")), ms);
    }),
  ]).finally(() => clearTimeout(timer));
};

// The bytes of a data: URL.
export const dataUrlBytes = (dataUrl) => {
  const binary = atob(dataUrl.slice(dataUrl.indexOf(",") + 1));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const drawJpeg = async (image, width, height, quality) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("No canvas");
  // A see-through PNG gets a white background, as JPEG has none.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, width, height);
  // toDataURL makes the JPEG at once (about 0.1 s for a 12 MP photo).
  // Chrome's toBlob waits for the page to be idle, which took 4 s a photo
  // on our orders page.
  const dataUrl = canvas.toDataURL("image/jpeg", quality);
  // Phones keep little memory for canvases: free this one now.
  canvas.width = 0;
  canvas.height = 0;
  if (!dataUrl.startsWith("data:image/jpeg")) throw new Error("No JPEG");
  return new Blob([dataUrlBytes(dataUrl)], { type: "image/jpeg" });
};

// A 16 × 8 photo, black on its left half, marked "turn 90°" (EXIF orientation
// 6) as phones mark photos taken upright: the right way up it's 8 × 16, black
// on top.
const TURNED_PHOTO =
  "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4QAiRXhpZgAATU0AKgAAAAgAAQESAAMAAAABAAYAAAAAAAD/" +
  "2wBDABALDA4MChAODQ4SERATGCgaGBYWGDEjJR0oOjM9PDkzODdASFxOQERXRTc4UG1RV19iZ2hnPk1xeXBkeFxlZ2P/" +
  "wAALCAAIABABAREA/8QAFQABAQAAAAAAAAAAAAAAAAAABwj/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/9oACAEBAAA/AD9QD//Z";

let uprightCheck = null;

/**
 * Whether this browser draws photos the right way up, as every browser since
 * 2020 does (Chrome 81, Safari 13.1 and iOS 13.4, Firefox 77). An older one
 * would save a shrunk photo on its side, so photos go there as they are.
 * Checked once, on a tiny photo marked as turned.
 */
export const drawsPhotosUpright = () => {
  if (!uprightCheck) {
    uprightCheck = withTimeout(
      new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
          try {
            const { naturalWidth: width, naturalHeight: height } = image;
            if (!(width < height)) {
              resolve(false);
              return;
            }
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext("2d");
            context.drawImage(image, 0, 0, width, height);
            const x = Math.floor(width / 2);
            const top = context.getImageData(x, 1, 1, 1).data[0];
            const bottom = context.getImageData(x, height - 2, 1, 1).data[0];
            resolve(top < 96 && bottom > 160);
          } catch (error) {
            reject(error);
          }
        };
        image.onerror = () => reject(new Error("Can't open the test photo"));
        image.src = TURNED_PHOTO;
      }),
      5000,
    ).catch(() => false);
  }
  return uprightCheck;
};

/**
 * Opens a photo in the browser: { width, height } the right way up (browsers
 * apply the phone's rotation flag), toJpeg(width, height, quality) → Blob,
 * and close() to let it go. Rejects when the browser can't open it, or would
 * draw it on its side.
 */
export const openImage = async (file) => {
  if (!(await drawsPhotosUpright())) {
    throw new Error("This browser would save the photo on its side");
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const close = () => {
      image.onload = null;
      image.onerror = null;
      URL.revokeObjectURL(url);
    };
    image.onload = () =>
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
        toJpeg: (width, height, quality) =>
          drawJpeg(image, width, height, quality),
        close,
      });
    image.onerror = () => {
      close();
      reject(new Error("This browser can't open the photo"));
    };
    image.src = url;
  });
};

/**
 * The file to upload for `file`: a shrunk JPEG named like the photo
 * ("IMG_1234.jpg") when it's a photo bigger than it needs to be, otherwise
 * `file` itself. Never rejects. `options` override PHOTO_OPTIONS; `open`
 * (default openImage) is how a photo is opened, for tests.
 */
export const shrinkPhoto = async (file, options = {}) => {
  const { maxSide, quality, keepBytes, timeoutMs, open } = {
    ...PHOTO_OPTIONS,
    open: openImage,
    ...options,
  };
  if (!isPhoto(file)) return file;
  let image = null;
  try {
    image = await withTimeout(
      Promise.resolve().then(() => open(file)),
      timeoutMs,
    );
    const { width, height } = image;
    if (!width || !height) return file;
    if (Math.max(width, height) <= maxSide && file.size <= keepBytes) {
      return file;
    }
    const size = fitWithin(width, height, maxSide);
    const blob = await withTimeout(
      image.toJpeg(size.width, size.height, quality),
      timeoutMs,
    );
    if (!blob?.size || blob.size >= file.size) return file;
    let body = blob;
    if (isJpeg(file)) {
      try {
        const details = readPhotoDetails(
          await readBytes(file.slice(0, PHOTO_DETAILS_BYTES)),
        );
        if (details) body = withPhotoDetails(await readBytes(blob), details);
      } catch {
        // The photo still goes, without its details.
      }
    }
    return new File([body], jpegName(file.name), {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  } finally {
    try {
      image?.close?.();
    } catch {
      // Nothing to let go of.
    }
  }
};

// Photos are shrunk one at a time: opening a 12–50 MP photo takes 50–200 MB,
// and phones run out of memory when several open at once.
let queue = Promise.resolve();

/**
 * shrinkPhoto, queued behind the photos already being shrunk anywhere in the
 * app. Never rejects.
 */
export const preparePhoto = (file, options) => {
  const job = queue.then(() => shrinkPhoto(file, options));
  queue = job.catch(() => undefined);
  return job;
};
